import { ConvertedCurrencyBalance, CurrencyBalance, UnconvertedCurrencyBalance } from "@/components/l10n/Currency.tsx";
import TC from "@/components/table/Table.module.css";
import { VatText } from "@/components/VatText.tsx";
import { siteCurrencyAtom } from "@/config.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { TotalExclVat } from "@/modules/billing/invoice/InvoiceView.tsx";
import { getCalculatedFees } from "@/modules/billing/payment/fees.ts";
import type { MethodFees } from "@/modules/billing/paymentMethods.ts";
import type { SiteAmount } from "@/modules/currency/currencyTypes.ts";
import { DisplayCurrencyRate } from "@/modules/currency/DisplayCurrencyRate.tsx";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { intersperse } from "@warrenio/utils/collections/intersperse";
import { useAtomValue } from "jotai/react";
import { useNumberFormatter } from "react-aria";

export function CurrencyPaymentFees({
    account,
    amount,
    fees,
    isInvoicePayment = false,
}: {
    amount: SiteAmount;
    account: EBillingAccount;
    fees: MethodFees;
    isInvoicePayment?: boolean;
}) {
    const siteCurrency = useAtomValue(siteCurrencyAtom);
    const currency = useCurrentCurrency();

    const { vat_percentage } = account.account;

    const percentFmt = useNumberFormatter({
        style: "percent",
        maximumFractionDigits: 10,
    });

    const { baseFee, feePercent, total, vatAmount, transactionFee } = getCalculatedFees(fees, amount, vat_percentage);

    const feeParts = intersperse(
        " + ",
        filterFalse([
            baseFee !== 0 && <ConvertedCurrencyBalance value={baseFee} currency={currency.code} />,
            feePercent !== 0 && percentFmt.format(feePercent / 100),
        ]),
    );

    const hasDisplayColumn = !currency.isSite;

    if (amount === total && currency.isSite) {
        // No fees to display, and no currency conversion - this table is not needed.
        // Assumes actual amount is always displayed somewhere else.
        return null;
    }

    const amountTitle = isInvoicePayment ? (
        <TotalExclVat />
    ) : (
        <Trans comment="How much to top up | POS: Noun">Top up amount</Trans>
    );
    return (
        <div>
            {hasDisplayColumn && (
                <div className="pb-3">
                    <DisplayCurrencyRate />
                </div>
            )}
            <div className="lightBlock p-2">
                <table className={cn(TC.Fees, "w-full")}>
                    {hasDisplayColumn && (
                        // Only need to display table headers if there is more than one currency column
                        <thead>
                            <tr>
                                <th />

                                <th className="text-right">
                                    <Trans>Display currency</Trans>
                                </th>

                                <th className="text-right">
                                    <Trans>Base currency</Trans>
                                </th>
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        <tr>
                            <td>{amountTitle}</td>
                            {hasDisplayColumn && (
                                <td className="text-right">
                                    <CurrencyBalance value={amount} />
                                </td>
                            )}
                            <td className="text-right">
                                <UnconvertedCurrencyBalance value={amount} currency={siteCurrency} />
                            </td>
                        </tr>
                        {!!transactionFee && (
                            <tr>
                                <td>
                                    <Trans>Transaction fee</Trans>
                                </td>
                                {hasDisplayColumn && (
                                    <td className="text-right">
                                        <CurrencyBalance value={transactionFee} />
                                    </td>
                                )}
                                <td className="text-right">
                                    <UnconvertedCurrencyBalance value={transactionFee} currency={siteCurrency} />
                                </td>
                            </tr>
                        )}
                        {!!vatAmount && (
                            <tr>
                                <td>
                                    <VatText /> {percentFmt.format(vat_percentage / 100)}
                                </td>
                                {hasDisplayColumn && (
                                    <td className="text-right">
                                        <CurrencyBalance value={vatAmount} />
                                    </td>
                                )}
                                <td className="text-right">
                                    <UnconvertedCurrencyBalance value={vatAmount} currency={siteCurrency} />
                                </td>
                            </tr>
                        )}
                        {amount !== total && (
                            <tr>
                                <td>
                                    <Trans>Total charge</Trans>
                                </td>
                                {hasDisplayColumn && (
                                    <td className="text-right">
                                        <CurrencyBalance value={total} />
                                    </td>
                                )}
                                <td className="text-right font-bold">
                                    <UnconvertedCurrencyBalance value={total} currency={siteCurrency} />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!!baseFee && (
                <p className="pt-2 color-muted">
                    <Trans>Transaction fee</Trans> {...feeParts}
                </p>
            )}
        </div>
    );
}
